/**
 * The Files tab.
 *
 * Drag-and-drop with a per-file progress bar, and the §8 attachment
 * attestation as a **checkbox in the upload dialog, required before the
 * presign call and before the multipart initiation** — the large-file path is
 * exactly the one it exists for. It is not a banner to click past: uploads are
 * the one path the pattern scan cannot see.
 *
 * An upload interrupted before confirmation is **recoverable, not destroyed**:
 * the row stays `pending`, and the hourly sweeper promotes it once it sees the
 * object. So a failure to confirm is reported as "still finishing", never as
 * "it failed" — telling the user their 250 MB upload was lost when the sweeper
 * is about to confirm it is the worst of both.
 */

import { useRef, useState } from 'react';
import { Download, FileUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { AttachmentAttestation } from '@/features/export/ExportAttestation';
import { uploadAttachment } from './upload';
import { api, ApiError } from '@/lib/api';
import { useApiMutation, useApiQuery } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { useToast } from '@/lib/toast';
import { formatDateTime } from '@/lib/format';

interface AttachmentRow {
  id: string;
  file_name: string;
  content_type: string;
  size_bytes: number | null;
  upload_state: 'pending' | 'confirmed';
  uploaded_at: string;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} bytes`;
}

export function AttachmentsPanel({
  entityType,
  entityId,
  canWrite,
}: {
  entityType: string;
  entityId: string;
  canWrite: boolean;
}): React.ReactElement {
  const reference = useReferenceData();
  const { push } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [attestation, setAttestation] = useState<boolean | null>(null);
  const [progress, setProgress] = useState<{ name: string; percent: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const key = ['attachments', entityType, entityId];
  const list = useApiQuery<{ items: AttachmentRow[] }>(
    key,
    `/api/attachments?entityType=${entityType}&entityId=${entityId}`,
  );

  const remove = useApiMutation<Record<string, unknown>, string>(
    (id) => ({ path: `/api/attachments/${id}`, method: 'DELETE' }),
    { invalidate: [key] },
  );

  const start = async (): Promise<void> => {
    if (pendingFile === null || attestation !== false) return;
    const file = pendingFile;
    setPendingFile(null);
    setAttestation(null);
    setProgress({ name: file.name, percent: 0 });

    try {
      await uploadAttachment({
        file,
        target: { entityType, entityId },
        exportControlledDataWillBeEntered: false,
        multipartThresholdBytes: reference.multipart_threshold_bytes,
        onProgress: ({ loaded, total }) =>
          setProgress({ name: file.name, percent: Math.round((loaded / total) * 100) }),
      });
      push({ tone: 'success', title: `${file.name} uploaded` });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CONTENT_TYPE_NOT_ALLOWED') {
        push({
          tone: 'error',
          title: `${file.type || 'That file type'} is not accepted`,
          body: `Allowed: ${reference.attachment_content_type_allowlist.join(', ')}. Archives are excluded — a .zip defeats an allowlist entirely.`,
        });
      } else if (error instanceof ApiError && error.code === 'EXPORT_BLOCKED') {
        push({ tone: 'error', title: error.message });
      } else if (error instanceof ApiError) {
        push({ tone: 'error', title: error.message });
      } else {
        // The row stays `pending` and the sweeper promotes it once it sees the
        // object. Do not tell the user it failed.
        push({
          tone: 'warning',
          title: `${file.name} did not finish confirming`,
          body: 'The bytes may already be in S3. Nothing is lost — the hourly sweeper confirms an upload it can see, and only removes a row whose object never arrived.',
        });
      }
    } finally {
      setProgress(null);
      void list.refetch();
    }
  };

  /**
   * Ask for the presigned URL with our credentials attached, then navigate to
   * it. A plain link to `/api/attachments/:id/download` would carry no token —
   * it lives in memory, not in a cookie — and would 401. The bytes still never
   * touch the API: the URL points at S3 and carries
   * `response-content-disposition=attachment` as a signed parameter, so the
   * browser saves the file rather than rendering it.
   */
  const download = async (id: string): Promise<void> => {
    try {
      const { url } = await api<{ url: string }>(`/api/attachments/${id}/download`);
      window.location.assign(url);
    } catch (error) {
      push({
        tone: 'error',
        title: error instanceof ApiError ? error.message : 'The download could not be prepared.',
      });
    }
  };

  const items = list.data?.items ?? [];

  return (
    <section className="space-y-3">
      {canWrite && (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            const file = event.dataTransfer.files[0];
            if (file !== undefined) setPendingFile(file);
          }}
          className={`rounded-lg border border-dashed p-6 text-center text-sm ${
            dragOver ? 'border-primary bg-accent' : 'text-muted-foreground'
          }`}
        >
          <FileUp className="mx-auto mb-2 h-5 w-5" aria-hidden="true" />
          <p>Drop a file here, or</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => inputRef.current?.click()}
          >
            Choose a file
          </Button>
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) setPendingFile(file);
              event.target.value = '';
            }}
          />
          <p className="mt-2 text-[11px]">
            Files go straight from your browser to storage and never through the API. Over{' '}
            {formatBytes(reference.multipart_threshold_bytes)} they upload in parts, and a failed
            part retries on its own.
          </p>
        </div>
      )}

      {progress !== null && (
        <div className="rounded-md border p-3 text-xs">
          <p className="mb-1">
            {progress.name} — {progress.percent}%
          </p>
          <div className="h-1.5 w-full rounded-full bg-muted">
            <div
              className="h-1.5 rounded-full bg-primary transition-[width]"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      )}

      <ul className="divide-y rounded-md border">
        {items.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
            <span className="min-w-0">
              <span className="block truncate font-medium">{row.file_name}</span>
              <span className="text-[11px] text-muted-foreground">
                {row.content_type} · {formatBytes(row.size_bytes)} ·{' '}
                {formatDateTime(row.uploaded_at)}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {row.upload_state === 'pending' && (
                <Badge
                  variant="risk"
                  title="The bytes may still be arriving. The hourly sweeper confirms it once it sees the object."
                >
                  Finishing
                </Badge>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void download(row.id)}
                disabled={row.upload_state !== 'confirmed'}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                Download
              </Button>
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(row.id)}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only">Delete {row.file_name}</span>
                </Button>
              )}
            </span>
          </li>
        ))}
        {items.length === 0 && (
          <li className="px-3 py-6 text-center text-xs text-muted-foreground">No files yet.</li>
        )}
      </ul>

      {pendingFile !== null && (
        <Dialog
          open
          onClose={() => {
            setPendingFile(null);
            setAttestation(null);
          }}
          title={`Upload ${pendingFile.name}`}
          description={`${formatBytes(pendingFile.size)} · ${pendingFile.type || 'unknown type'}`}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setPendingFile(null);
                  setAttestation(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={() => void start()} disabled={attestation !== false}>
                Upload
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <AttachmentAttestation value={attestation} onChange={setAttestation} />
            {attestation === true && (
              <p className="rounded-md border border-amber-600/40 bg-amber-50 p-3 text-xs text-amber-950">
                Then this file cannot be uploaded here. No credential is issued, so the content
                never reaches storage.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Accepted types: {reference.attachment_content_type_allowlist.join(', ') || '—'}.
              Archives are excluded. The file name and type are recorded; the content is never
              scanned, because scanning it would mean reading it.
            </p>
          </div>
        </Dialog>
      )}
    </section>
  );
}
